import { useTranslation } from "@ferrivia/i18n";
import type { HomePhase, JourneyRung, JourneyRungId } from "@ferrivia/api-contract";

/* The words for a place on the journey, in one catalog for both surfaces.
 *
 * This exists for the reason @ferrivia/lab-ui's analyteCopy does: two apps
 * name the same thing, and naming it twice is how the two names drift. The
 * customer's home screen and the clinic console's Journey tab must agree on
 * what a phase is called — a clinician talking someone through their screen
 * is only useful if the words are the ones on it — and because the copy
 * lives in this package's own `journey` namespace rather than either app's,
 * the same phase comes out in Finnish for a customer and in English for a
 * clinician from one source.
 *
 * Every t() call below takes a literal key: i18next-parser cannot follow a
 * key through a variable, so these are registries of copy strings, not of
 * key strings — the same shape as the client's PhaseHeader before it read
 * from here.
 */

export interface JourneyCopy {
  /** What the customer's own home screen says at this phase. The clinic
      shows it verbatim so both sides of a conversation see one wording. */
  headerFor(phase: HomePhase): string;
  /** The short name of a rung on the spine, for the clinic's ladder. */
  stepFor(rung: Pick<JourneyRung, "id" | "phase">): string;
  /** What opens a rung the customer has not reached. */
  gateFor(rungId: JourneyRungId): string;
}

export function useJourneyCopy(): JourneyCopy {
  const { t } = useTranslation("journey");

  const headers: Record<HomePhase, string> = {
    default: t("header.default", "Tervetuloa Ferriviaan"),
    awaiting_draw: t("header.awaiting_draw", "Varaa näytteenottoaikasi"),
    sampled: t("header.sampled", "Näytteesi on matkalla laboratorioon"),
    result_ready: t("header.result_ready", "Ensimmäinen tuloksesi on saapunut"),
    second_result: t("header.second_result", "Katsotaan miten on mennyt"),
    raising_started: t("header.raising_started", "Lähdetään nostamaan arvoja yhdessä"),
    not_now: t("header.not_now", "Pidetään tilanne vakaana"),
    // The infusion track's own title, not a greeting: this phase's home
    // screen is the getting-started checklist for a journey whose clinical
    // half happens at Sydänkeskus, and naming the journey is what orients a
    // client who arrived from the lead form (Ville, 2026-09-08).
    lead: t("header.lead", "Rautainfuusio Ferrivian kautta"),
    screener: t("header.screener", "Esitiedot kesken"),
    labs_pending: t("header.labs_pending", "Seuraavaksi laboratoriokokeet"),
    ready_to_book: t("header.ready_to_book", "Olet valmis vastaanotolle"),
  };

  /* Keyed by rung, not by phase, and deliberately so: phaseForStage maps
     `prereg` to the phase `default`, so a phase-keyed lookup would label the
     first rung "welcome". The Day-0 fork is the one rung with three
     spellings — unanswered, and one per branch. */
  const steps = {
    prereg: t("step.prereg", "Tili luotu"),
    awaiting_draw: t("step.awaiting_draw", "Laboratoriopaketti tilattu"),
    result_ready: t("step.result_ready", "Ensimmäinen tulos saapunut"),
    // Unanswered, so this names the pending decision, not either outcome —
    // the fork is the customer's to make (see stepFor's day0 branch below).
    day0: t("step.day0", "Päättää arvojen nostamisesta"),
    day0_yes: t("step.day0_yes", "Aloitti arvojen nostamisen"),
    day0_no: t("step.day0_no", "Ei nosta arvoja juuri nyt"),
    second_result: t("step.second_result", "Toinen tulos saapunut"),
  };

  const gates: Record<JourneyRungId, string> = {
    // Names the two tests the panel actually runs (ferritin, CRP), not just
    // "a lab panel" — see apps/client's STORE_PRODUCTS "lab-panel" entry,
    // the one product this rung's purchase can be.
    awaiting_draw: t("gate.awaiting_draw", "Ferritiini ja CRP laboratoriopaketista."),
    prereg: t("gate.prereg", "Asiakas on luonut tilin."),
    result_ready: t("gate.result_ready", "Yksi laboratorion raportoima tai asiakkaan itse kirjaama mittaus."),
    day0: t(
      "gate.day0",
      "Asiakas vastaa päivän 0 kysymykseen. Vain hän voi vastata — tätä ei siirrä mikään tieto eikä hoitava henkilö.",
    ),
    second_result: t("gate.second_result", "Toinen mittaus, vähintään 30 päivää ensimmäisen raportoinnista."),
  };

  return {
    headerFor: (phase) => headers[phase],
    stepFor: ({ id, phase }) => {
      if (id !== "day0") {
        return steps[id];
      }
      if (phase === "raising_started") return steps.day0_yes;
      if (phase === "not_now") return steps.day0_no;
      // Phase is null until the customer answers, which is the fork's whole
      // point: the rung exists and its branch is theirs to choose.
      return steps.day0;
    },
    gateFor: (rungId) => gates[rungId],
  };
}
